/**
 * Manager for stage configuration state with persistence and EventBus integration.
 * Single source of truth for stage controls (count, scale, variants).
 */

import { Observable } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager.js";
import { DEFAULT_RUNE_STAGE_CONFIG } from "./RuneManager.js";

/**
 * Configuration for a stage (count, scale, variants)
 *
 * Supports both single-select (variant) and multi-select (variants) modes:
 * - variant: Single string for single-select stages
 * - variants: Array for multi-select stages
 */
export interface StageConfig {
  stageId: string;
  count: number;
  scale: number;
  /** Single-select variant */
  variant?: string;
  /** Multi-select variants (e.g., rune types) */
  variants?: string[];
  /** GLB model key for parameterized stages (e.g., drill GLB variants) */
  glbKey?: string;
}

/**
 * Default configurations per stage type
 */
const DEFAULT_CONFIGS: Record<string, Omit<StageConfig, "stageId">> = {
  bob: { count: 1, scale: 1 },
  drill: { count: 1, scale: 30 }, // GLB is ~0.25m real size, scale up for visibility; glbKey comes from route
  gem: { count: 5, scale: 1 },
  ns: { count: 1, scale: 3 }, // GLB models are ~0.3 units, scale up for visibility; glbKey comes from route
  rune: {
    count: DEFAULT_RUNE_STAGE_CONFIG.count,
    scale: DEFAULT_RUNE_STAGE_CONFIG.scale,
    variants: [...DEFAULT_RUNE_STAGE_CONFIG.variants],
  },
};

/**
 * localStorage key prefix for stage configs
 */
const STORAGE_KEY_PREFIX = "tony.nl/stage-config/";

/**
 * Manages stage configuration state with Observable notifications,
 * localStorage persistence, and EventBus integration.
 *
 * Used by both React demo pages (via useStageConfig hook) and
 * Inspector v2 (via useObservableState).
 */
export class StageConfigManager extends BaseManager {
  private readonly _configChangedObservable =
    new Observable<StageConfig | null>();
  private currentConfig: StageConfig | null = null;

  /**
   * Observable that fires when config changes.
   * Subscribe with: manager.configChangedObservable.add(callback)
   */
  get configChangedObservable(): Observable<StageConfig | null> {
    return this._configChangedObservable;
  }

  /**
   * Current stage config. Alias for getCurrentConfig() to support
   * useObservableState pattern: `useObservableState(() => manager.config, ...)`
   */
  get config(): StageConfig | null {
    return this.currentConfig;
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("stageConfigManagerReady", {});
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this._configChangedObservable.clear();
    this.currentConfig = null;
  }

  /**
   * Get the current stage config
   */
  getCurrentConfig(): StageConfig | null {
    return this.currentConfig;
  }

  /**
   * Get default config for a stage type
   */
  getDefaultConfig(stageId: string): StageConfig {
    const defaults = DEFAULT_CONFIGS[stageId] ?? { count: 5, scale: 1 };
    return { stageId, ...defaults };
  }

  /**
   * Called when a stage is loaded. Loads config from localStorage or defaults.
   * @param stageId The stage ID that was loaded
   * @param overrides Optional partial config to merge (e.g., glbKey from route query params)
   */
  onStageLoaded(stageId: string, overrides?: Partial<StageConfig>): void {
    this.ensureNotDisposed();

    // Try to load from localStorage
    const stored = this.loadFromStorage(stageId);
    if (stored) {
      this.currentConfig = { ...stored, ...overrides };
    } else {
      // Use defaults
      this.currentConfig = { ...this.getDefaultConfig(stageId), ...overrides };
    }

    // Notify observers
    this._configChangedObservable.notifyObservers(this.currentConfig);
  }

  /**
   * Update the current config with partial values.
   * Persists to localStorage and emits stageConfigChanged event.
   * @param partial Partial config to merge
   */
  updateConfig(partial: Partial<StageConfig>): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[StageConfigManager] No current config to update");
      return;
    }

    // Merge partial into current
    this.currentConfig = {
      ...this.currentConfig,
      ...partial,
      // Ensure stageId doesn't change
      stageId: this.currentConfig.stageId,
    };

    // Persist to localStorage
    this.saveToStorage(this.currentConfig);

    // Notify Observable subscribers (React hooks, Inspector)
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event (StageContext listens to reload stage)
    this.emit("stageConfigChanged", this.currentConfig);
  }

  /**
   * Set the full config (replaces current)
   * @param config The new config
   */
  setConfig(config: StageConfig): void {
    this.ensureNotDisposed();

    this.currentConfig = config;

    // Persist to localStorage
    this.saveToStorage(config);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("stageConfigChanged", this.currentConfig);
  }

  /**
   * Reset config to defaults for the current stage
   */
  resetToDefaults(): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[StageConfigManager] No current config to reset");
      return;
    }

    const stageId = this.currentConfig.stageId;

    // Clear from localStorage
    this.clearStorage(stageId);

    // Reset to defaults
    this.currentConfig = this.getDefaultConfig(stageId);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("stageConfigChanged", this.currentConfig);
  }

  /**
   * Load config from localStorage
   */
  private loadFromStorage(stageId: string): StageConfig | null {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as Partial<StageConfig>;

      // Validate and merge with defaults
      const defaults = this.getDefaultConfig(stageId);
      return {
        ...defaults,
        ...parsed,
        stageId, // Ensure stageId is correct
      };
    } catch {
      console.warn(`[StageConfigManager] Failed to load config for ${stageId}`);
      return null;
    }
  }

  /**
   * Save config to localStorage
   */
  private saveToStorage(config: StageConfig): void {
    try {
      const key = STORAGE_KEY_PREFIX + config.stageId;
      // Only store non-default values to keep storage minimal
      const toStore: Partial<StageConfig> = {
        count: config.count,
        scale: config.scale,
      };
      if (config.variant) {
        toStore.variant = config.variant;
      }
      if (config.variants) {
        toStore.variants = config.variants;
      }
      localStorage.setItem(key, JSON.stringify(toStore));
    } catch {
      console.warn(
        `[StageConfigManager] Failed to save config for ${config.stageId}`,
      );
    }
  }

  /**
   * Clear config from localStorage
   */
  private clearStorage(stageId: string): void {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  }
}
